import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, DAY_NAMES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { IcalFeedPanel } from "@/components/IcalFeedPanel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Check, Copy, Save, ExternalLink, Upload, ImageOff } from "lucide-react";
import { useContact } from "@/components/ContactModal";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import BookingQrCode from "@/components/BookingQrCode";
import PlanChangeDialog from "@/components/PlanChangeDialog";
import { toast } from "sonner";
import { PANEL_CLS, PAGE_EYEBROW_CLS, PAGE_H1_CLS, PRIMARY_BTN_CLS } from "@/components/premium";
import { BOOKING_COVER_PRESETS } from "@/lib/bookingCoverPresets";

const PLANS = [
  {
    id: "demo",
    name: "Demo",
    price: 0,
    showVat: false,
    features: [
      "Fiktiv demo-bedrift",
      "Offentlig bookingside",
      "Kundeliste med demodata",
      "Bookinglenke og QR-kode",
      "Begrenset bruk",
    ],
  },
  {
    id: "basic",
    name: "Basic",
    price: 199,
    showVat: true,
    features: [
      "Ubegrenset bookinger",
      "1 bruker / leder",
      "Offentlig bookingside, kalender og kundeliste",
      "Åpningstider og stengte dager",
      "Bookinglenke og QR-kode",
      "Eksport av kunder og bookinger",
      "Enkel support",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: 399,
    showVat: true,
    features: [
      "Alt i Basic",
      "Flere ansatte / brukere",
      "Personal- og teamoversikt",
      "Kundeoversikt, historikk og notater",
      "Kunder å følge opp",
      "Prioritert support og hjelp med oppsett",
      "+99 kr/mnd per ekstra ansatt",
    ],
  },
];

export default function Settings() {
  const { user, refresh } = useAuth();
  const { open: openContact } = useContact();
  const [biz, setBiz] = useState(null);
  const [hasServices, setHasServices] = useState(null);  // null = loading, bool otherwise
  const [saving, setSaving] = useState(false);
  const [profileName, setProfileName] = useState(user?.name || "");
  const [profileSaving, setProfileSaving] = useState(false);
  const [planChangeOpen, setPlanChangeOpen] = useState(false);

  // Shared community demo (demo@clientflow.no on Nordlys Frisør) — these
  // users see an alt notice instead of the per-tenant "Be om pakkeendring"
  // flow, since the account isn't theirs to upgrade.
  const isSharedDemo = user?.email === "demo@clientflow.no";
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    try { const res = await api.get("/business"); setBiz(res.data); }
    catch { toast.error("Kunne ikke laste bedrift"); }
    // Tracks whether the tenant has at least one ACTIVE service. Drives the
    // "Bookinglenken din er klar, men kunder kan ikke booke før du har lagt
    // inn minst én tjeneste"-warning under the booking-link card.
    try {
      const r = await api.get("/services");
      const active = (r.data || []).some((s) => s.active !== false);
      setHasServices(active);
    } catch {
      setHasServices(true);  // fail-open: don't nag on transient errors
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (user?.name) setProfileName(user.name); }, [user?.name]);

  // Persist closed_dates immediately so chip add/remove can't get stuck in an
  // "unsaved" state where the UI shows chips but the backend never received
  // them (the original two-step Legg til → Lagre endringer flow was easy to
  // miss and could let bookings slip through on intended closed days).
  //
  // We send ONLY the closed-date fields here so we don't accidentally clobber
  // other edits the Leder may have made in the same form session.
  const persistClosedDates = async (closed_dates, closed_date_reasons) => {
    try {
      const res = await api.put("/business", { closed_dates, closed_date_reasons });
      // Sync local state from the authoritative server response.
      setBiz((prev) => ({
        ...prev,
        closed_dates: res.data.closed_dates || [],
        closed_date_reasons: res.data.closed_date_reasons || {},
      }));
      return true;
    } catch {
      toast.error("Kunne ikke lagre stengte dager");
      return false;
    }
  };

  const saveProfile = async () => {
    if (!profileName.trim()) return toast.error("Navn kan ikke være tomt");
    setProfileSaving(true);
    try {
      await api.put("/auth/me", { name: profileName.trim() });
      await refresh();
      toast.success("Profil oppdatert");
    } catch { toast.error("Kunne ikke lagre profil"); }
    finally { setProfileSaving(false); }
  };

  if (!biz) return (
    <div className="space-y-8 anim-in">
      <div>
        <div className="h-3 w-20 bg-primary/10 rounded animate-pulse" />
        <div className="mt-2 h-9 w-72 bg-primary/10 rounded animate-pulse" />
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="rounded-2xl border border-border p-6 lg:col-span-2 space-y-3">
          {[0,1,2,3,4].map(i => <div key={i} className="h-12 bg-primary/10 rounded animate-pulse" />)}
        </div>
        <div className="rounded-2xl border border-border p-6 space-y-3">
          <div className="h-12 bg-primary/10 rounded animate-pulse" />
        </div>
      </div>
    </div>
  );

  const update = (patch) => setBiz({ ...biz, ...patch });
  const updateHour = (idx, patch) => {
    const hours = [...(biz.opening_hours || [])];
    hours[idx] = { ...hours[idx], ...patch };
    setBiz({ ...biz, opening_hours: hours });
  };

  const save = async () => {
    setSaving(true);
    try {
      // Keep this payload in sync with BusinessUpdate (backend/models.py).
      // Add new editable fields BOTH here and in the BusinessUpdate model so
      // they actually persist. Server-managed fields (id, owner_id, slug,
      // plan, logo_url, created_at) are intentionally not included.
      await api.put("/business", {
        name: biz.name,
        address: biz.address,
        phone: biz.phone,
        email: biz.email,
        description: biz.description,
        opening_hours: biz.opening_hours,
        closed_dates: biz.closed_dates || [],
        closed_date_reasons: biz.closed_date_reasons || {},
        review_url: biz.review_url || null,
        // "" (empty string) clears back to the legacy fallback header — the
        // backend converts it to None server-side.
        booking_cover_preset: biz.booking_cover_preset || "",
      });
      toast.success("Lagret");
    } catch { toast.error("Kunne ikke lagre"); } finally { setSaving(false); }
  };

  const onLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Velg en bildefil");
    if (file.size > 300 * 1024) return toast.error("Bildet er for stort (max 300KB)");
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.put("/business/logo", { data_url: reader.result });
        toast.success("Logo lagret");
        load();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Kunne ikke laste opp");
      }
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = async () => {
    try {
      await api.put("/business", { logo_url: "" });
      toast.success("Logo fjernet");
      load();
    } catch { toast.error("Kunne ikke fjerne"); }
  };

  const bookingUrl = `${window.location.origin}/book/${biz.slug}`;

  const isDemo = biz.slug === "nordlys-frisor";
  const isPlatformAdmin = biz.is_admin === true;

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <p className={PAGE_EYEBROW_CLS}>Innstillinger</p>
          <h1 className={PAGE_H1_CLS}>Bedrift & abonnement</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Du redigerer <span className="font-medium text-foreground">{biz.name}</span>
            <span className="text-muted-foreground/70"> ({biz.slug})</span>.
          </p>
        </div>
        {isDemo && (
          <Badge variant="outline" className="rounded-full border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800" data-testid="demo-data-badge">
            Demo-data
          </Badge>
        )}
        {isPlatformAdmin && (
          <Badge variant="outline" className="rounded-full border-primary/40 bg-primary/5 text-primary" data-testid="admin-biz-badge">
            ClientFlow-administrator
          </Badge>
        )}
      </div>

      {isPlatformAdmin && (
        <div
          className="rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-foreground/90"
          data-testid="admin-biz-banner"
        >
          <p className="font-medium">Du administrerer ClientFlow-bedriften.</p>
          <p className="mt-1 text-muted-foreground">
            Innstillingene her (inkl. stengte dager) gjelder kun{" "}
            <span className="font-medium text-foreground">{biz.name}</span> og påvirker ikke
            kundenes egne booking-sider. For å hjelpe en kundebedrift må Lederen logge inn
            på sin egen konto. Du finner kundebehandling under{" "}
            <a href="/admin/leads" className="text-primary underline underline-offset-2">Forespørsler</a>.
          </p>
        </div>
      )}

      {/* Min profil */}
      <Card className={`${PANEL_CLS}`} data-testid="profile-card">
        <CardHeader><CardTitle className="font-heading">Min profil</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Navn</Label>
              <Input
                className="rounded-xl mt-1.5"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                data-testid="profile-name-input"
                maxLength={120}
              />
            </div>
            <div>
              <Label>E-post</Label>
              <Input
                className="rounded-xl mt-1.5"
                value={user?.email || ""}
                readOnly
                disabled
                data-testid="profile-email-readonly"
              />
              <p className="text-xs text-muted-foreground mt-1">Endring av e-post er ikke tilgjengelig enda</p>
            </div>
          </div>
          <Button onClick={saveProfile} disabled={profileSaving || profileName.trim() === (user?.name || "")} className={`${PRIMARY_BTN_CLS} h-10`} data-testid="save-profile-btn">
            <Save className="w-4 h-4 mr-1" /> {profileSaving ? "Lagrer..." : "Lagre profil"}
          </Button>
        </CardContent>
      </Card>

      {/* Endre passord */}
      <ChangePasswordCard />

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className={`${PANEL_CLS} lg:col-span-2`}>
          <CardHeader><CardTitle className="font-heading">Bedriftsprofil</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              {/* Logo preview matches the public booking badge's visual
                  language — same object-contain + soft inner padding so
                  what the owner sees here is what their customers see. */}
              <div className="w-20 h-20 rounded-2xl bg-accent/40 flex items-center justify-center overflow-hidden border border-border ring-1 ring-border/60 p-1.5" data-testid="biz-logo-preview">
                {biz.logo_url ? (
                  <img src={biz.logo_url} alt="Logo" className="w-full h-full object-contain" data-testid="biz-logo" />
                ) : (
                  <ImageOff className="w-7 h-7 text-muted-foreground" strokeWidth={1.5} />
                )}
              </div>
              <div className="flex-1">
                <Label className="block mb-1">Logo (valgfritt, max 300KB)</Label>
                <p className="text-xs text-muted-foreground mb-2" data-testid="logo-upload-help">
                  Anbefalt: kvadratisk bilde, minst 512 × 512 px. PNG med
                  transparent bakgrunn gir ofte best resultat.
                </p>
                <div className="flex gap-2">
                  <label className="cursor-pointer inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm hover:bg-accent" data-testid="logo-upload-label">
                    <Upload className="w-4 h-4" /> Last opp
                    <input type="file" accept="image/*" className="hidden" onChange={onLogoChange} data-testid="logo-upload-input" />
                  </label>
                  {biz.logo_url && (
                    <Button variant="outline" size="sm" className="rounded-xl" onClick={removeLogo} data-testid="logo-remove-btn">
                      Fjern
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Bedriftsnavn</Label><Input className="rounded-xl mt-1.5" value={biz.name || ""} onChange={(e) => update({ name: e.target.value })} data-testid="biz-name-input" /></div>
              <div><Label>Telefon</Label><Input className="rounded-xl mt-1.5" value={biz.phone || ""} onChange={(e) => update({ phone: e.target.value })} data-testid="biz-phone-input" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>E-post</Label><Input type="email" className="rounded-xl mt-1.5" value={biz.email || ""} onChange={(e) => update({ email: e.target.value })} data-testid="biz-email-input" /></div>
              <div><Label>Adresse</Label><Input className="rounded-xl mt-1.5" value={biz.address || ""} onChange={(e) => update({ address: e.target.value })} data-testid="biz-address-input" /></div>
            </div>
            <div><Label>Beskrivelse</Label><Textarea rows={3} className="rounded-xl mt-1.5" value={biz.description || ""} onChange={(e) => update({ description: e.target.value })} data-testid="biz-desc-input" /></div>

            {/* Premium-only: Anmeldelseslenke som legges inn i Takk-for-besøket-mailen. */}
            <div data-testid="review-url-section">
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <Label>Anmeldelseslenke</Label>
                <span className="text-[10px] uppercase tracking-widest font-semibold text-primary">Premium</span>
              </div>
              <Input
                type="url"
                placeholder="https://g.page/r/..."
                className={`rounded-xl mt-1.5 ${biz.plan !== "premium" ? "opacity-60" : ""}`}
                value={biz.review_url || ""}
                disabled={biz.plan !== "premium"}
                onChange={(e) => update({ review_url: e.target.value })}
                data-testid="biz-review-url-input"
              />
              <p className="text-xs text-muted-foreground mt-1.5">
                {biz.plan === "premium"
                  ? "Lenke til Google Reviews, Trustpilot eller annen anmeldelseside. Legges automatisk inn i Takk-for-besøket-mailen."
                  : "Premium-funksjon. Lenken vises i den automatiske Takk-for-besøket-mailen — slik at fornøyde kunder kan legge igjen en anmeldelse i ett klikk."}
              </p>
            </div>

            <IcalFeedPanel plan={biz.plan} />

            {/* Booking-side utseende — preset picker (CSS-only, no upload).
                Visible to every owner regardless of plan. Selecting a tile
                updates local state immediately; persistence happens on the
                main "Lagre"-button further down (same as the rest of this
                card's fields). Picking "Standard" clears the field so the
                public booking page falls back to the legacy emerald layout. */}
            <div data-testid="booking-cover-preset-section">
              <Label className="mb-2 block">Booking-side utseende</Label>
              <p className="text-xs text-muted-foreground mb-3">
                Velg et premium-bakgrunns-tema for hodebildet på din offentlige
                bookingside. Logoen din vises som et eget element på toppen.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {/* "Standard" tile — null preset, falls back to the legacy
                    emerald glow. Newly created businesses default here. */}
                <button
                  type="button"
                  onClick={() => update({ booking_cover_preset: "" })}
                  data-testid="booking-cover-preset-none"
                  className={`relative rounded-xl border p-2.5 text-left transition-all ${
                    !biz.booking_cover_preset
                      ? "border-primary ring-2 ring-primary/30"
                      : "border-border hover:border-primary/40"
                  }`}
                >
                  <div
                    className="h-14 rounded-lg mb-2 border border-border bg-gradient-to-br from-emerald-500/[0.06] via-emerald-500/[0.03] to-transparent"
                  />
                  <div className="text-xs font-medium leading-tight">Standard</div>
                  <div className="text-[10px] text-muted-foreground leading-tight">
                    Subtil emerald-glød
                  </div>
                </button>

                {BOOKING_COVER_PRESETS.map((p) => {
                  const active = biz.booking_cover_preset === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => update({ booking_cover_preset: p.id })}
                      data-testid={`booking-cover-preset-${p.id}`}
                      title={p.description}
                      className={`relative rounded-xl border p-2.5 text-left transition-all ${
                        active
                          ? "border-primary ring-2 ring-primary/30"
                          : "border-border hover:border-primary/40"
                      }`}
                    >
                      <div
                        className="h-14 rounded-lg mb-2 border border-border/60 overflow-hidden relative"
                        style={p.headerStyle}
                      >
                        <div
                          className="absolute -top-4 -left-4 w-20 h-20 rounded-full blur-2xl opacity-80"
                          style={p.glowStyle}
                        />
                      </div>
                      <div className="text-xs font-medium leading-tight">{p.label}</div>
                      <div className="text-[10px] text-muted-foreground leading-tight line-clamp-1">
                        {p.description}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-3">
                <a
                  href={`/book/${biz.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 underline-offset-2 hover:underline"
                  data-testid="booking-cover-preset-preview-link"
                >
                  <ExternalLink className="w-3 h-3" /> Forhåndsvis offentlig bookingside
                </a>
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Åpningstider</Label>
              <div className="space-y-2">
                {(biz.opening_hours || []).map((h, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="w-16 sm:w-20 text-muted-foreground">{DAY_NAMES[h.day]}</span>
                    <Switch checked={!h.closed} onCheckedChange={(v) => updateHour(i, { closed: !v })} data-testid={`day-open-${i}`} />
                    {h.closed ? (
                      <span className="text-muted-foreground text-sm">Stengt</span>
                    ) : (
                      <>
                        <Input type="time" className="rounded-xl w-24 sm:w-28" value={h.open} onChange={(e) => updateHour(i, { open: e.target.value })} />
                        <span className="text-muted-foreground">–</span>
                        <Input type="time" className="rounded-xl w-24 sm:w-28" value={h.close} onChange={(e) => updateHour(i, { close: e.target.value })} />
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Stengte dager (ferier o.l.)</Label>
              <div className="flex flex-wrap gap-2 mb-3">
                {(biz.closed_dates || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">Ingen stengte dager.</p>
                ) : (
                  (biz.closed_dates || []).map((d, i) => {
                    const reason = (biz.closed_date_reasons || {})[d] || "";
                    return (
                      <span
                        key={d}
                        className="inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-3 py-1 text-xs"
                        data-testid="closed-date-chip"
                      >
                        <span>
                          {new Date(d + "T00:00:00").toLocaleDateString("nb-NO", { day: "2-digit", month: "short", year: "numeric" })}
                          {reason && (
                            <span className="ml-1 text-foreground/70">— {reason}</span>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={async () => {
                            const reasons = { ...(biz.closed_date_reasons || {}) };
                            delete reasons[d];
                            const next = (biz.closed_dates || []).filter((x) => x !== d);
                            // Optimistic local update for snappy UI, then persist.
                            update({ closed_dates: next, closed_date_reasons: reasons });
                            const ok = await persistClosedDates(next, reasons);
                            if (ok) toast.success("Stengt dag fjernet");
                          }}
                          className="ml-1 text-foreground/60 hover:text-foreground"
                          aria-label="Fjern"
                          data-testid={`remove-closed-${i}`}
                        >×</button>
                      </span>
                    );
                  })
                )}
              </div>
              <ClosedRangeAdder
                onAdd={async ({ from, to, reason }) => {
                  // Expand inclusive [from, to] into individual YYYY-MM-DD strings.
                  // IMPORTANT: format from local getters, NEVER `toISOString()`.
                  // `new Date("2026-07-02T00:00:00")` is local midnight; in CEST
                  // that is 2026-07-01T22:00Z, so `.toISOString().slice(0,10)`
                  // would silently shift the saved date one day backwards.
                  const start = new Date(from + "T00:00:00");
                  const end = new Date((to || from) + "T00:00:00");
                  if (isNaN(start) || isNaN(end) || end < start) {
                    toast.error("Ugyldig datointervall");
                    return;
                  }
                  const fmt = (d) => {
                    const y = d.getFullYear();
                    const m = String(d.getMonth() + 1).padStart(2, "0");
                    const day = String(d.getDate()).padStart(2, "0");
                    return `${y}-${m}-${day}`;
                  };
                  const newDates = new Set(biz.closed_dates || []);
                  const reasons = { ...(biz.closed_date_reasons || {}) };
                  const cursor = new Date(start);
                  let added = 0;
                  while (cursor <= end) {
                    const iso = fmt(cursor);
                    if (!newDates.has(iso)) added += 1;
                    newDates.add(iso);
                    if (reason) reasons[iso] = reason; else delete reasons[iso];
                    cursor.setDate(cursor.getDate() + 1);
                  }
                  const arr = Array.from(newDates).sort();
                  // Optimistic local update then persist to backend immediately.
                  // This avoids the previous two-step trap where chips appeared
                  // saved client-side but were never sent until the Leder also
                  // remembered to click "Lagre endringer".
                  update({ closed_dates: arr, closed_date_reasons: reasons });
                  const ok = await persistClosedDates(arr, reasons);
                  if (ok) {
                    toast.success(
                      added === 0
                        ? "Allerede lagt til"
                        : `Lagret ${added} stengt dag${added === 1 ? "" : "er"}`
                    );
                  }
                }}
              />
              <p className="text-xs text-muted-foreground mt-2">
                Endringer i stengte dager lagres automatisk.
              </p>
            </div>
            <Button onClick={save} disabled={saving} className={`${PRIMARY_BTN_CLS} h-10`} data-testid="save-business-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Lagrer..." : "Lagre endringer"}
            </Button>
          </CardContent>
        </Card>

        <Card className={PANEL_CLS}>
          <CardHeader><CardTitle className="font-heading">Din bookinglenke</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">Del denne lenken med kundene dine så kan de booke selv.</p>
            {hasServices === false && (
              <div
                className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-3 text-sm"
                data-testid="no-services-warning"
              >
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-300 shrink-0" strokeWidth={1.75} />
                <div className="flex-1">
                  <div className="font-medium">Klar til å dele — men nesten ferdig.</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Bookinglenken din er klar, men kunder kan ikke booke før du har lagt inn minst én tjeneste.
                  </div>
                </div>
                <Link to="/tjenester" className="shrink-0">
                  <Button className={`${PRIMARY_BTN_CLS} h-9`} data-testid="no-services-cta">
                    Legg til tjeneste
                  </Button>
                </Link>
              </div>
            )}
            <div className="p-3 rounded-xl bg-muted border border-border font-mono text-xs break-all" data-testid="booking-url">
              {bookingUrl}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-xl flex-1" onClick={() => { navigator.clipboard.writeText(bookingUrl); toast.success("Kopiert"); }} data-testid="copy-link-btn">
                <Copy className="w-4 h-4 mr-1" /> Kopier bookinglenke
              </Button>
              <a href={bookingUrl} target="_blank" rel="noreferrer" className="flex-1">
                <Button variant="outline" className="rounded-xl w-full">
                  <ExternalLink className="w-4 h-4 mr-1" /> Åpne
                </Button>
              </a>
            </div>
            <BookingQrCode bookingUrl={bookingUrl} slug={biz.slug} businessName={biz.name} />
          </CardContent>
        </Card>
      </div>

      {/* Plans */}
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight">Abonnement</h2>
        <div className="text-muted-foreground text-sm flex items-center gap-2 mt-1">
          <span data-testid="current-plan-label">Nåværende pakke:</span>
          <Badge variant="outline" className="rounded-full" data-testid="current-plan-badge">
            {PLANS.find((p) => p.id === biz.plan)?.name}
          </Badge>
        </div>

        {isSharedDemo ? (
          // Shared community demo — no per-user upgrade path. Direct the
          // user toward getting their own dedicated demo account via the
          // shared contact dialog. The mailto stays as a copy/fallback.
          (() => {
            const openSharedDemoContact = () => openContact({
              source: "settings-shared-demo-notice",
              defaultName: user?.name || "",
              defaultEmail: user?.email || "",
              defaultCompany: biz.name || "",
              defaultInterest: "annet",
              defaultMessage:
                "[Felles demo] Jeg bruker demoen og ønsker:\n" +
                "- egen testkonto / oppgradering / spørsmål\n\n" +
                `Bedrift: ${biz.name || "—"}\n` +
                `Nåværende pakke: Demo\n` +
                `Innlogget: ${user?.email || "—"}`,
            });
            return (
              <div
                className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-5 dark:bg-amber-950/40 dark:border-amber-700/60"
                data-testid="shared-demo-notice"
              >
                <div className="flex gap-3">
                  <div className="shrink-0 mt-0.5 w-9 h-9 rounded-xl bg-amber-200/80 dark:bg-amber-800/60 flex items-center justify-center">
                    <AlertTriangle
                      className="w-5 h-5 text-amber-900 dark:text-amber-100"
                      strokeWidth={2}
                    />
                  </div>
                  <div className="flex-1 min-w-0 space-y-2 text-sm leading-relaxed">
                    <p className="font-semibold text-amber-950 dark:text-amber-50">
                      Dette er en felles demo
                    </p>
                    <p className="text-amber-900 dark:text-amber-100">
                      Ikke legg inn ekte kundeinformasjon. Demoen kan bli ryddet eller nullstilt.
                    </p>
                    <p className="text-amber-900 dark:text-amber-100">
                      Trenger du egen testkonto, vil oppgradere eller har spørsmål?{" "}
                      <button
                        type="button"
                        onClick={openSharedDemoContact}
                        className="font-semibold underline underline-offset-2 decoration-amber-700/60 hover:decoration-amber-900 dark:decoration-amber-300/60 dark:hover:decoration-amber-100 cursor-pointer"
                        data-testid="shared-demo-contact-cta"
                      >
                        Kontakt ClientFlow
                      </button>
                      .
                    </p>
                    <div className="pt-1 flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        onClick={openSharedDemoContact}
                        className="rounded-xl"
                        data-testid="shared-demo-contact-btn"
                      >
                        Kontakt ClientFlow
                      </Button>
                      <span
                        className="text-xs text-amber-900/85 dark:text-amber-200/90"
                        data-testid="shared-demo-mailto-fallback"
                      >
                        eller send e-post til:{" "}
                        <a
                          href="mailto:hei@clientflow.no"
                          className="underline underline-offset-2 decoration-amber-700/60 hover:decoration-amber-900 dark:decoration-amber-300/60 dark:hover:decoration-amber-100"
                          data-testid="shared-demo-mailto-link"
                        >
                          hei@clientflow.no
                        </a>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()
        ) : (
          <div
            className="mt-4 rounded-2xl border border-border bg-muted/30 p-4 text-sm"
            data-testid="plan-change-cta-card"
          >
            <p className="font-semibold">Trenger du en annen pakke?</p>
            <p className="mt-0.5 text-muted-foreground">
              Pakkeendringer gjøres av ClientFlow. Send en kort forespørsel, så tar vi kontakt.
            </p>
            <Button
              className="mt-3 rounded-xl"
              onClick={() => setPlanChangeOpen(true)}
              data-testid="open-plan-change-dialog"
            >
              Be om pakkeendring
            </Button>
          </div>
        )}

        <div className="mt-5 grid md:grid-cols-3 gap-4">
          {PLANS.map((p) => {
            const active = p.id === biz.plan;
            return (
              <Card key={p.id} className={`rounded-2xl card-hover ${active ? "border-primary border-2" : "border-border"}`} data-testid={`billing-plan-${p.id}`}>
                <CardContent className="p-6">
                  <h3 className="font-heading text-xl font-semibold">{p.name}</h3>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="font-heading text-3xl font-bold">{p.price}</span>
                    <span className="text-sm text-muted-foreground">{p.price === 0 ? "kr" : "kr/mnd"}</span>
                  </div>
                  {p.showVat ? (
                    <p className="text-xs text-muted-foreground">eks. mva</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Ingen kostnad</p>
                  )}
                  <ul className="mt-4 space-y-1.5 text-sm">
                    {p.features.map((f) => <li key={f} className="flex items-start gap-2"><Check className="w-3.5 h-3.5 mt-0.5 text-primary" /> {f}</li>)}
                  </ul>
                  {active && (
                    <Button
                      className="mt-5 w-full rounded-xl"
                      variant="outline"
                      disabled
                      data-testid={`plan-card-current-${p.id}`}
                    >
                      Nåværende pakke
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Pakkeendringer gjøres manuelt av ClientFlow. Du blir ikke fakturert automatisk.
        </p>
      </div>

      <p className="text-xs text-muted-foreground">Innlogget som {user?.email}. Bedrift: {biz.name}. · Backend: {BACKEND_URL}</p>

      <PlanChangeDialog
        open={planChangeOpen}
        onOpenChange={setPlanChangeOpen}
        business={biz}
        userEmail={user?.email}
        userName={user?.name}
      />
    </div>
  );
}

const REASON_PRESETS = ["Ferie", "Stengt", "Kurs", "Helligdag", "Sykdom"];

function ClosedRangeAdder({ onAdd }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [reason, setReason] = useState("");

  const submit = () => {
    if (!from) {
      toast.error("Velg en startdato");
      return;
    }
    onAdd({ from, to: to || from, reason: reason.trim() });
    setFrom(""); setTo(""); setReason("");
  };

  return (
    <div
      className="rounded-xl border border-border bg-background p-3 space-y-2"
      data-testid="closed-range-form"
    >
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
        <div>
          <Label className="text-xs">Fra</Label>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-xl mt-1"
            data-testid="closed-range-from"
          />
        </div>
        <div>
          <Label className="text-xs">Til <span className="text-muted-foreground">(valgfritt)</span></Label>
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            min={from || undefined}
            className="rounded-xl mt-1"
            data-testid="closed-range-to"
          />
        </div>
        <div>
          <Label className="text-xs">Årsak <span className="text-muted-foreground">(valgfritt)</span></Label>
          <Input
            type="text"
            list="closed-reason-presets"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ferie, Kurs, Helligdag…"
            className="rounded-xl mt-1"
            maxLength={40}
            data-testid="closed-range-reason"
          />
          <datalist id="closed-reason-presets">
            {REASON_PRESETS.map((r) => <option key={r} value={r} />)}
          </datalist>
        </div>
        <Button
          type="button"
          onClick={submit}
          className={`${PRIMARY_BTN_CLS} h-10`}
          data-testid="closed-range-add"
        >
          Legg til
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {REASON_PRESETS.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setReason(r)}
            className={`text-[11px] rounded-full px-2.5 py-0.5 border transition-colors ${
              reason === r
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background border-border text-muted-foreground hover:bg-accent"
            }`}
            data-testid={`reason-preset-${r}`}
          >
            {r}
          </button>
        ))}
      </div>
    </div>
  );
}
